/**
 * In-app notifications inbox hook.
 *
 * Reads from `public.notifications` (RLS-scoped to current user) with
 * realtime updates so the bell badge and inbox stay live.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type NotificationCategory =
  | "call"
  | "billing"
  | "chat"
  | "security"
  | "promo"
  | "system";

export interface AppNotification {
  id: string;
  user_id: string;
  category: NotificationCategory;
  title: string;
  body: string | null;
  deep_link: string | null;
  metadata: Record<string, unknown>;
  read_at: string | null;
  delivered_push: boolean;
  created_at: string;
}

const KEY = (uid?: string) => ["notifications", uid] as const;

export function useNotifications(limit = 50) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEY(user?.id),
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async (): Promise<AppNotification[]> => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as AppNotification[];
    },
  });

  // Realtime subscription
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    const topic = `notifications:${userId}`;

    // Defensive: if a channel with this exact topic is still cached on the
    // client (StrictMode double-mount, hot reload, or a previous render that
    // didn't clean up), remove it BEFORE creating + binding handlers.
    // Supabase v2 throws "cannot add `postgres_changes` callbacks ... after
    // `subscribe()`" if we re-bind on an already-subscribed channel - which
    // surfaces as the red "Something went wrong" screen on the Account tab.
    for (const existing of supabase.getChannels()) {
      if (existing.topic === `realtime:${topic}`) {
        supabase.removeChannel(existing);
      }
    }

    const channel = supabase
      .channel(topic)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: KEY(userId) });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, qc]);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(user?.id) }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .is("read_at", null)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(user?.id) }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("notifications").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(user?.id) }),
  });

  const items = query.data ?? [];
  const unreadCount = items.filter((n) => !n.read_at).length;

  return {
    items,
    unreadCount,
    isLoading: query.isLoading,
    error: query.error,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
    remove: remove.mutate,
  };
}
